import Vue from 'vue';
import Router, { NavigationGuard } from 'vue-router';
import AuthModule from './store/AuthModule';
import { getCustomersPrescriber } from './api/api';
import get from 'lodash/get';
import MetaDatatable from './models/MetaDatatable';

Vue.use(Router);

const publicGuard: NavigationGuard = async (to, from, next) => {
    await AuthModule.init();
    if (!AuthModule.initialized) {
        return;
    }

    if (AuthModule.logged) {
        next({ name: 'home' });
    } else {
        next();
    }
};





const restrictedRoutesForStarters = [
    'prescriberMapSettings', // /pluginMap
    'prescriber-invoices',   // /factures
    'sponsorship',           // /sponsorship
    'subscription'           // /abonnement
];


const authGuard: NavigationGuard = async (to, from, next) => {
    await AuthModule.init();
    if (!AuthModule.initialized) {
        return;
    }

    if (!AuthModule.logged) {
        next({ name: 'home' });
    } else {
        // Block restricted users
        if (AuthModule.isRestricted && to.name && restrictedRoutesForStarters.includes(to.name)) {
            console.warn(`Access denied to ${to.name} for restricted users.`);
            next({ name: 'home' });
            return;
        }

        // ✅ AJOUTER ICI - Trial guard
        if (
            AuthModule.isTrialActive === true &&
            !AuthModule.isSubscriptionActive &&
            !AuthModule.roles.includes('ROLE_ADMIN') &&
            !sessionStorage.getItem('trial_completed') &&
            to.name !== 'Trial' &&
            to.name !== 'subscription'
        ) {
            next({ name: 'Trial' });
            return;
        }

        // Bloc existant inchangé
        if (!AuthModule.isSubscribed && (to.name == 'homePrescriber' || to.name == 'lotDetail')) {
            next({ name: 'subscription' });
        } else {
            if (to.meta.service_type && to.meta.service_type !== localStorage.getItem('service')) {
                next({ name: 'prospects' });
            }
            next();
        }
    }
};



const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    scrollBehavior() {
        return { x: 0, y: 0 };
    },
    routes: [
        {
            path: '',
            name: 'home',
            beforeEnter: authGuard,
            redirect: to => {
                if (to.meta.service_type && to.meta.service_type !== localStorage.getItem('service')) {
                    return { name: 'prospects' }
                }

                if (AuthModule.roles.includes('ROLE_ADMIN')) {
                    return { name: 'users' };
               } else if (AuthModule.roles.includes('ROLE_PRESCRIBER')) {
                    // Rediriger vers Trial seulement si pas encore complété dans cette session
                    if (AuthModule.isTrialActive === true && !sessionStorage.getItem('trial_completed')) {
                        return { name: 'Trial' };
                    }

                    if (!localStorage.getItem('service')) {
                        localStorage.setItem('service', 'immo');
                    }

                    if (localStorage.getItem('service') !== 'immo') {
                        return { name: 'createProspect' };
                    }

                    return { name: 'homePrescriber' };
                } else {
                    return { name: 'login' };
                }
            },
        },
        {
            path: '',
            component: () => import('./layouts/main/Main.vue'),
            beforeEnter: authGuard,
            children: [
                {
                    path: '/contrats',
                    name: 'contrats',
                    component: () => import('./views/Contracts.vue'),
                    meta: {
                        name: 'contrats',
                        rootUrl: '/contrats',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Contrats', active: true },
                        ],
                    },
                },
                {
                    path: '/contrats/:mode/:type',
                    name: 'addContrats',
                    component: () => import('./views/ContractsAdd.vue'),
                    meta: {
                        name: 'addContrats',
                        rootUrl: '/contrats/add/:type',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Contrats', active: true },
                        ],
                    },
                },
                {
                    path: '/articles',
                    name: 'articles',
                    component: () => import('./views/Articles.vue'),
                    meta: {
                        name: 'articles',
                        rootUrl: '/articles',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'articles', active: true },
                        ],
                    },
                },
                {
                    path: '/automatisation',
                    name: 'automatisation',
                    component: () => import('./views/Automation.vue'),
                    meta: {
                        name: 'automatisation',
                        rootUrl: '/automatisation',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'automatisation', active: true },
                        ],
                    },
                },
                {
                    path: '/profile',
                    name: 'profile',
                    component: () => import('./views/Profile.vue'),
                    meta: {
                        name: 'profile',
                        rootUrl: '/users',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Mon Profil', active: true },
                        ],
                    },
                },
                {
                    path: '/users/add',
                    name: 'addUser',
                    component: () => import('./views/UserForm.vue'),
                    meta: {
                        rootUrl: '/users',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Utilisateurs', url: '/' },
                            { title: 'Ajouter un utilisateur', active: true },
                        ],
                    },
                },
                {
                    path: '/users/edit/:id',
                    name: 'editUser',
                    component: () => import('./views/UserForm.vue'),
                    meta: {
                        rootUrl: '/users',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Utilisateurs', url: '/' },
                            { title: 'Modifier un utilisateur', active: true },
                        ],
                    },
                },
                {
                    path: '/users',
                    name: 'users',
                    component: () => import('./views/Users.vue'),
                    meta: {
                        rootUrl: '/users',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Utilisateurs', active: true },
                        ],
                    },
                },
                {
                    path: '/companies/add',
                    name: 'addCompany',
                    component: () => import('./views/CompanyForm.vue'),
                    meta: {
                        rootUrl: '/companies',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Entreprises', url: '/companies' },
                            { title: 'Ajouter une entreprise', active: true },
                        ],
                    },
                },
                {
                    path: '/companies/edit/:id',
                    name: 'editCompany',
                    component: () => import('./views/CompanyForm.vue'),
                    meta: {
                        rootUrl: '/companies',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Entreprises', url: '/companies' },
                            { title: 'Modifier une entreprise', active: true },
                        ],
                    },
                },
                {
                    path: '/companies',
                    name: 'companies',
                    component: () => import('./views/Companies.vue'),
                    meta: {
                        rootUrl: '/companies',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Entreprises', active: true },
                        ],
                    },
                },
                {
                    path: '/programs/add',
                    name: 'addProgram',
                    component: () => import('./views/ProgramForm.vue'),
                    meta: {
                        rootUrl: '/programs',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Programmes', url: '/programs' },
                            { title: 'Ajouter un programme', active: true },
                        ],
                    },
                },
                {
                    path: '/programs/edit/:id',
                    name: 'editProgram',
                    component: () => import('./views/ProgramForm.vue'),
                    meta: {
                        rootUrl: '/programs',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Programmes', url: '/programs' },
                            { title: 'Modifier un programme', active: true },
                        ],
                    },
                },
                {
                    path: '/programs/:programID',
                    name: 'showProgram',
                    component: () => import('./views/ProgramShow.vue'),
                    meta: {
                        rootUrl: '/programs',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Programmes', url: '/programs' },
                            { title: 'Fiche du programme', active: true },
                        ],
                    },
                },
                {
                    path: '/programs',
                    name: 'programs',
                    component: () => import('./views/Programs.vue'),
                    meta: {
                        rootUrl: '/programs',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Programmes', active: true },
                        ],
                    },
                },
                {
                    path: '/programs/:programID/lot/edit/:lotID',
                    name: 'editLot',
                    component: () => import('./views/LotForm.vue'),
                    meta: {
                        rootUrl: '/programs',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Programmes', url: '/programs' },
                            { title: 'Fiche du programme', url: '/' },
                            { title: 'Editer Lot', active: true },
                        ],
                    },
                },
                {
                    path: '/programs/:programID/lot/add',
                    name: 'addLot',
                    component: () => import('./views/LotForm.vue'),
                    meta: {
                        rootUrl: '/programs',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Programmes', url: '/programs' },
                            { title: 'Fiche du programme', url: '/' },
                            { title: 'Ajouter Lot', active: true },
                        ],
                    },
                },
                {
                    path: '/prescribers',
                    name: 'prescribers',
                    component: () => import('./views/Prescribers.vue'),
                    meta: {
                        rootUrl: '/prescribers',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Prescripteurs', active: true },
                        ],
                    },
                },
                {
                    path: '/prescribers/edit/:id',
                    name: 'editPrescriber',
                    component: () => import('./views/PrescriberForm.vue'),
                    meta: {
                        rootUrl: '/prescribers',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Prescripteurs', url: '/prescribers' },
                            { title: 'Modifier un prescripteur', active: true },
                        ],
                    },
                },
                {
                    path: '/customers',
                    name: 'customers',
                    component: () => import('./views/Customers.vue'),
                    meta: {
                        rootUrl: '/customers',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Clients', active: true },
                        ],
                    },
                },
                {
                    path: '/customers/edit/:id',
                    name: 'editCustomer',
                    component: () => import('./views/CustomerForm.vue'),
                    meta: {
                        rootUrl: '/customers',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Clients', url: '/customers' },
                            { title: 'Editer Client', active: true },
                        ],
                    },
                },
                {
                    path: '/denounces',
                    name: 'denounces',
                    component: () => import('./views/Denounces.vue'),
                    meta: {
                        rootUrl: '/denounces',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Dénonces', active: true },
                        ],
                    },
                },

                {
                    path: '/admin/trials',
                    name: 'adminTrials',
                    component: () => import('./views/AdminTrials.vue'),
                    beforeEnter: (to, from, next) => {
                        // Double check for Admin role specifically for this route
                        if (AuthModule.roles.includes('ROLE_ADMIN')) {
                            next();
                        } else {
                            next({ name: 'home' });
                        }
                    },
                    meta: {
                        rootUrl: '/admin/trials',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Administration', url: '/admin' }, // Optional
                            { title: 'Gestion Formation (Trial)', active: true },
                        ],
                    },
                },


                {
                    path: '/tracking-connections',
                    name: 'tracking-connections',
                    component: () => import('./views/TrackingConnections.vue'),
                    meta: {
                        rootUrl: '/tracking-connections',
                        breadcrumb: [
                        { title: 'Home', url: '/' },
                        { title: 'Connexions', active: true },
                        ],
                    },
                    },


                {
                    path: '/options',
                    name: 'options',
                    component: () => import('./views/Options.vue'),
                    meta: {
                        rootUrl: '/options',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Options', active: true },
                        ],
                    },
                },
                {
                    path: '/bookings',
                    name: 'bookings',
                    component: () => import('./views/Bookings.vue'),
                    meta: {
                        service_type: 'immo',
                        rootUrl: '/bookings',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Réservations', active: true },
                        ],
                    },
                },
                {
                    path: '/bookings/:bookingID',
                    name: 'editBookingAdmin',
                    component: () => import('./views/BookingAdmin.vue'),
                    meta: {
                        rootUrl: '/bookings',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Réservations', url: '/bookings' },
                            { title: 'Réservation', active: true },
                        ],
                    },
                },
                {
                    path: '/notifications',
                    name: 'notifications',
                    component: () => import('./views/Notifications.vue'),
                    meta: {
                        rootUrl: '/notifications',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Notifications', active: true },
                        ],
                    },
                },
                {
                    path: '/notifications/add',
                    name: 'addNotification',
                    component: () => import('./views/NotificationForm.vue'),
                    meta: {
                        rootUrl: '/notifications',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Notifications', url: '/notifications' },
                            { title: 'Ajouter une notification', active: true },
                        ],
                    },
                },
                {
                    path: '/notifications/edit/:id',
                    name: 'editNotification',
                    component: () => import('./views/NotificationForm.vue'),
                    meta: {
                        rootUrl: '/notifications',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Notifications', url: '/notifications' },
                            {
                                title: 'Modifier une notification',
                                active: true,
                            },
                        ],
                    },
                },
                {
                    path: '/invoices',
                    name: 'invoices',
                    component: () => import('./views/Invoices.vue'),
                    meta: {
                        rootUrl: '/invoices',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Factures', active: true },
                        ],
                    },
                },
                {
                    path: '/payments',
                    name: 'payments',
                    component: () => import('./views/Payments.vue'),
                    meta: {
                        rootUrl: '/payments',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Encaissements', active: true },
                        ],
                    },
                },
                {
                    path: '/subscriptions/edit/:id',
                    name: 'editSubscription',
                    component: () => import('./views/SubscriptionForm.vue'),
                    meta: {
                        rootUrl: '/subscriptions',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Abonnements', url: '/subscriptions' },
                            { title: 'Modifier un abonnement', active: true },
                        ],
                    },
                },
                {
                    path: '/subscriptions',
                    name: 'subscriptions',
                    component: () => import('./views/Subscriptions.vue'),
                    meta: {
                        rootUrl: '/subscriptions',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Abonnements', active: true },
                        ],
                    },
                },
            ],
        },
        /**
         * Precriber
         */
        {
            path: '/services',
            name: 'Services',
            component: () =>
                import('@/views/prescriber/AfterLoginSelector.vue'),
            beforeEnter: authGuard,
            meta: {
                rootUrl: '/services',
                breadcrumb: [
                ],
            },
        },
        {
            path: '/trial',
            name: 'Trial',
            component: () =>
                import('@/views/prescriber/Trial.vue'),
            beforeEnter: authGuard,
            meta: {
                rootUrl: '/trial',
                breadcrumb: [],
            },
        },
        
        {
            path: '',
            component: () =>
                import('@/layouts/prescriber/PrescriberLayout.vue'),
            beforeEnter: authGuard,
            children: [
                {
                    path: '/',
                    name: 'homePrescriber',
                    component: () =>
                        import('@/views/prescriber/HomePrescriber.vue'),
                    beforeEnter: authGuard,
                    meta: {
                        service_type: 'immo',
                        rootUrl: '/',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Carte des programmes', active: true },
                        ],
                    },
                },
                {
                    path: '/demandes_finance',
                    name: 'FinanceRequest',
                    component: () =>
                        import('@/views/prescriber/FinanceRequest.vue'),
                    beforeEnter: authGuard,
                    meta: {
                        service_type: 'finance',
                        rootUrl: '/',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Demandes PlugFinance', active: true },
                        ],
                    },
                },
                {
                    path: '/demandes_credit',
                    name: 'CreditRequest',
                    component: () =>
                        import('@/views/prescriber/CreditRequest.vue'),
                    beforeEnter: authGuard,
                    meta: {
                        service_type: 'credit',
                        rootUrl: '/',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Demandes PlugCredit', active: true },
                        ],
                    },
                },
                {
                    path: '/demandes_data_and_lead/:slug?',
                    name: 'DataRequest',
                    component: () =>
                        import('@/views/prescriber/DataRequest.vue'),
                    beforeEnter: authGuard,
                    meta: {
                        service_type: 'lead',
                        rootUrl: '/',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Demandes PlugCredit', active: true },
                        ],
                    },
                },
                {
                    path: '/produits_et_services',
                    name: 'ProductAndServices',
                    component: () =>
                        import('@/views/prescriber/ProductAndServices.vue'),
                    beforeEnter: authGuard,
                    meta: {
                        service_type: 'finance',
                        rootUrl: '/',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'produits et services', active: true },
                        ],
                    },
                },
                /**
                 * Page Partenaires en cours de dev
                 * {
                    path: '/partners',
                    name: 'partners',
                    component: () =>
                        import('@/views/prescriber/partners.vue'),
                    beforeEnter: authGuard,
                    meta: {
                        rootUrl: '/',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Partenaires', active: true },
                        ],
                    },
                },*/
                /**
                 * Page Parrainage en cours de dev
                 */
                {
                    path: '/sponsorship',
                    name: 'sponsorship',
                    component: () =>
                        import('@/views/prescriber/sponsorship.vue'),
                    beforeEnter: authGuard,
                    meta: {
                        rootUrl: '/',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Parrainage', active: true },
                        ],
                    },
                },/**/
                {
                    path: 'details/:id',
                    name: 'programDetails',
                    component: () =>
                        import('./views/prescriber/ProgramDetails.vue'),
                    meta: {
                        rootUrl: '/programs',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Carte des programmes', url: '/' },
                            { title: 'Fiche du programme', active: true },
                        ],
                    },
                    beforeEnter: authGuard,
                },
                {
                    path: '/lotDetail',
                    name: 'lotDetail',
                    component: () =>
                        import('./views/prescriber/LotDetail.vue'),
                    meta: {
                        service_type: 'immo',
                        rootUrl: '/lotDetail',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Affichage par lot', active: true },
                        ],
                    },
                    beforeEnter: authGuard,
                },
                {
                    path: '/outils',
                    name: 'outils',
                    component: () =>
                        import('./views/prescriber/Outils.vue'),
                    meta: {
                        rootUrl: '/outils',
                    },
                    beforeEnter: authGuard,
                },
                {
                    path: '/prospects',
                    component: () => import('./views/prescriber/Prospects.vue'),
                    beforeEnter: authGuard,
                    children: [


                        {
                                path: '',
                                name: 'prospects',
                                component: () =>
                                    import(
                                        './components/prescriber/ProspectsPlaceholder.vue'
                                    ),
                                beforeEnter: async (to, from, next) => {
                                    next({ name: 'prospectsTable' });
                                },

                                meta: {
                                    rootUrl: '/',
                                    breadcrumb: [
                                        { title: 'Home', url: '/' },
                                        { title: 'Prospects', active: true },
                                    ],
                                },
                            },


                        {
                                path: 'table',
                                name: 'prospectsTable',
                                component: () => import('./components/prescriber/ProspectsTable.vue'),
                                meta: {
                                    rootUrl: '/',
                                    breadcrumb: [
                                        { title: 'Home', url: '/' },
                                        { title: 'Prospects', url: '/' },
                                        { title: 'Liste des prospects', active: true },
                                    ],
                                },
                                beforeEnter: authGuard,
                            },

                        {
                            path: 'create',
                            name: 'createProspect',
                            component: () =>
                                import(
                                    './components/prescriber/ProspectForm.vue'
                                ),
                            meta: {
                                rootUrl: '/',
                                breadcrumb: [
                                    { title: 'Home', url: '/' },
                                    { title: 'Prospects', url: '/' },
                                    { title: 'Prospect', active: true },
                                ],
                            },
                            beforeEnter: authGuard,
                        },
                        {
                            path: ':customerID',
                            name: 'showProspect',
                            component: () =>
                                import(
                                    './components/prescriber/ProspectsDetails.vue'
                                ),
                            meta: {
                                rootUrl: '/',
                                breadcrumb: [
                                    { title: 'Home', url: '/' },
                                    { title: 'Prospects', url: '/' },
                                    { title: 'Prospect', active: true },
                                ],
                            },
                            beforeEnter: authGuard,
                        },
                        {
                            path: ':customerID/edit',
                            name: 'editProspect',
                            component: () =>
                                import(
                                    './components/prescriber/ProspectForm.vue'
                                ),
                            meta: {
                                rootUrl: '/',
                                breadcrumb: [
                                    { title: 'Home', url: '/' },
                                    { title: 'Prospects', url: '/' },
                                    { title: 'Prospect', active: true },
                                ],
                            },
                            beforeEnter: authGuard,
                        },
                    ],
                },
                {
                    path: '/abonnement/:slug?',
                    name: 'subscription',
                    component: () =>
                        import('@/views/prescriber/SubscriptionPrescriber.vue'),
                    beforeEnter: authGuard,
                },
                {
                    path: '/factures',
                    name: 'prescriber-invoices',
                    component: () =>
                        import('@/views/prescriber/InvoicePrescriber.vue'),
                    beforeEnter: authGuard,
                },
                {
                    path: '/profil',
                    name: 'profilePrescriber',
                    component: () =>
                        import('@/views/prescriber/ProfilePrescriber.vue'),
                    beforeEnter: authGuard,
                    meta: {
                        rootUrl: '/',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Mon profil', active: true },
                        ],
                    },
                },

                {
                    path: '/webinar',
                    name: 'webinar',
                    component: () =>
                        import('@/views/prescriber/Webinar.vue'),
                    beforeEnter: authGuard,
                    meta: {
                        rootUrl: '/',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Webinar', active: true },
                        ],
                    },
                },
                {
                    path: '/settings/pluginMap',
                    name: 'prescriberMapSettings',
                    component: () =>
                        import('@/views/prescriber/PluginMapSettingsPrescriber.vue'),
                    beforeEnter: authGuard,
                    meta: {
                        rootUrl: '/',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Configuration du Plugin Carte', active: true },
                        ],
                    },
                },
                {
                    path: '/reservations',
                    name: 'bookingsPrescriber',
                    component: () =>
                        import('@/views/prescriber/BookingsPrescriber.vue'),
                    beforeEnter: authGuard,
                    meta: {
                        service_type: 'immo',
                        rootUrl: '/',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Réservations', active: true },
                        ],
                    },
                },
                {
                    path: '/mes-denounces',
                    name: 'denouncesPrescriber',
                    component: () =>
                        import('@/views/prescriber/DenouncesPrescriber.vue'),
                    beforeEnter: authGuard,
                    meta: {
                        service_type: 'immo',
                        rootUrl: '/',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Dénonces', active: true },
                        ],
                    },
                },
                {
                    path: '/mes-options',
                    name: 'optionsPrescriber',
                    component: () =>
                        import('@/views/prescriber/OptionsPrescriber.vue'),
                    beforeEnter: authGuard,
                    meta: {
                        service_type: 'immo',
                        rootUrl: '/',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Options', active: true },
                        ],
                    },
                },
                {
                    path: '/reservations/:bookingID',
                    name: 'bookingPrescriber',
                    component: () =>
                        import('@/views/prescriber/BookingPrescriber.vue'),
                    meta: {
                        rootUrl: '/',
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: 'Réservations', url: '/' },
                            { title: 'Réservation', active: true },
                        ],
                    },
                    beforeEnter: authGuard,
                },
            ],
        },
        {
            path: '',
            component: () => import('@/layouts/full-page/FullPage.vue'),
            beforeEnter: publicGuard,
            children: [
                {
                    path: '/connexion',
                    name: 'login',
                    component: () => import('@/views/pages/Login.vue'),
                },
                {
                    path: '/inscription',
                    name: 'register',
                    component: () => import('@/views/pages/Register.vue'),
                },
                {
                    path: '/pages/error-404',
                    name: 'pageError404',
                    component: () => import('@/views/pages/Error404.vue'),
                },
                {
                    path: '/motdepasseoublie',
                    name: 'pageForgotPassword',
                    component: () => import('@/views/pages/ForgotPassword.vue'),
                },
                {
                    path: '/reset-password',
                    name: 'pageResetPassword',
                    component: () => import('@/views/pages/ResetPassword.vue'),
                },
            ],
        },
        {
            // Pas de publicGuard ici : un utilisateur connecté doit pouvoir voir la page maintenance
            // (sinon il est renvoyé vers 'home' -> /users)
            path: '',
            component: () => import('@/layouts/full-page/FullPage.vue'),
            children: [
                {
                    path: '/maintenance',
                    name: 'pageMaintenance',
                    component: () => import('@/views/pages/Maintenance.vue'),
                },
            ],
        },
        {
            path: '*',
            redirect: '/pages/error-404',
        },
    ],
});



router.afterEach(() => {
    const appLoading = document.getElementById('loading-bg');
    if (appLoading) {
        appLoading.style.display = 'none';
    }
});

export default router;